import { ParcelResponseDTO } from "../api/response.types";
import {
  ParcelEventModel,
  ParcelFromToModel,
  ParcelLocationModel,
  ParcelRouteModel,
  ParcelTransformedEventModel,
} from "./types";
import { parcelErrorMessageMap, ParcelMessagesModel, ParcelStatusesModel } from "entities/shared/parcelCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceModel,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { MapRoute, MapRouteModel } from "entities/shared/mapRouteModel";
import { SubscriptionDataModel, SubscriptionModel } from "entities/shared/subscriptionModel";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { getUtcTimestamp } from "shared/utils/dateTime.utils";
import { isAppIntegration } from "shared/utils/url.utils";

import { SearchApiParser } from "../api/parser";

export const isParcelSearchShipmentModel = (
  model: Nullable<{ responseType: string }>
): model is ParcelSearchCardModel => model?.responseType === ShipmentVariantModel.ParcelSearch;

export class ParcelSearchCardModel extends BaseModel<SearchApiParser, ParcelResponseDTO> {
  public readonly responseType: ShipmentVariantModel;
  public readonly requestLimitReachedAppIntegration: boolean = false;
  public readonly requestLimitReachedWebApp: boolean = false;
  private readonly _subscription: SubscriptionModel | null = null;
  private _pol: string | null = null;
  private _pod: string | null = null;
  private _route: MapRoute | null = null;

  constructor(DTO: ParcelResponseDTO) {
    super({ apiParser: new SearchApiParser(DTO), DTO });

    this._subscription = new SubscriptionModel(this.apiParser.metadata);

    this.setLocations();

    this._route = new MapRoute(this.adaptRouteData());

    this.responseType = ShipmentVariantModel.ParcelSearch;
    this.requestLimitReachedAppIntegration = this.requestLimitReached && isAppIntegration;
    this.requestLimitReachedWebApp = this.requestLimitReached && !isAppIntegration;
  }

  get isSuccess(): boolean {
    return this.apiParser.isSuccess;
  }

  get requestLimitReached(): boolean {
    return this.apiParser.requestLimitReached;
  }

  get subscription(): SubscriptionDataModel | null {
    return this._subscription?.data ?? null;
  }

  get reference(): SearchReferenceModel {
    return this.apiParser.number;
  }

  get carrierCode(): string {
    return this.apiParser.carrierCode;
  }

  get status(): ValueOf<typeof ParcelStatusesModel> {
    return this.apiParser.status;
  }

  get carrierName(): string {
    return this.apiParser.carrierName;
  }

  get fromLocation(): Nullable<ParcelFromToModel> {
    return this.apiParser.fromLocation;
  }

  get toLocation(): Nullable<ParcelFromToModel> {
    return this.apiParser.toLocation;
  }

  get polCoordinate(): CoordinateModel | null {
    return this.apiParser.polCoordinate;
  }

  get podCoordinate(): CoordinateModel | null {
    return this.apiParser.podCoordinate;
  }

  get etd(): Nullable<DateTimeModel> {
    return this.apiParser.etd;
  }

  get atd(): Nullable<DateTimeModel> {
    return this.apiParser.atd;
  }

  get eta(): Nullable<DateTimeModel> {
    return this.apiParser.eta;
  }

  get ata(): Nullable<DateTimeModel> {
    return this.apiParser.ata;
  }

  get progress(): number {
    return this.calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta,
      status: this.status,
    });
  }

  private setLocations() {
    if (!this.apiParser?.routes?.length) return;
    this._pol = this.getLocationName(this.apiParser.routes[0].from);
    this._pod = this.getLocationName(this.apiParser.routes[this.apiParser.routes.length - 1].to);
  }

  get fromDirection(): string | null {
    return this._pol;
  }

  get toDirection(): string | null {
    return this._pod;
  }

  public getLocationName(location: Nullable<ParcelLocationModel>): string | null {
    if (!location) return null;

    // Priority: name -> city (with country_code) -> address -> locode
    if (location.name) return location.name;
    if (location.state) {
      return location.country_code ? `${location.country_code} ${location.state}` : location.state;
    }
    if (location.locode) return location.locode;

    return null;
  }

  private calcProgress({
    startDate,
    endDate,
    status,
  }: {
    startDate: Nullable<DateTimeModel>;
    endDate: Nullable<DateTimeModel>;
    status: ValueOf<typeof ParcelStatusesModel>;
  }): number {
    const notStartedStatusesSet = new Set<ValueOf<typeof ParcelStatusesModel>>([
      ParcelStatusesModel.BOOKED,
      ParcelStatusesModel.RECEIVED,
    ]);
    const completedStatusesSet = new Set<ValueOf<typeof ParcelStatusesModel>>([
      ParcelStatusesModel.NOTIFIED,
      ParcelStatusesModel.DELIVERED,
      ParcelStatusesModel.ARRIVED,
    ]);
    if (notStartedStatusesSet.has(status)) return 0;
    if (completedStatusesSet.has(status)) return 100;
    if (status === ParcelStatusesModel.CANCELLED) return 0;

    if (!startDate || !endDate || startDate === endDate) return 0;

    const start: number = getUtcTimestamp(startDate);
    const end: number = getUtcTimestamp(endDate);
    const today: number = getUtcTimestamp(new Date());

    const totalTime = Math.abs(end - start);
    const elapsedTime = Math.abs(today - start);

    let percentageElapsed: number;

    if (elapsedTime <= 0) {
      percentageElapsed = 0;
    } else if (elapsedTime >= totalTime) {
      percentageElapsed = 100;
    } else {
      percentageElapsed = Math.round((elapsedTime / totalTime) * 100);
    }

    return percentageElapsed;
  }

  public searchErrorText(defaultText: string): string | null {
    const searchMessage = this.apiParser.responseStatus;
    if (searchMessage === ParcelMessagesModel.OK) return null;
    return parcelErrorMessageMap[searchMessage] ?? defaultText;
  }

  get events(): ParcelEventModel[] {
    return this.apiParser.events.map((event) => {
      return {
        ...event,
        transportType: event.transport_mode,
        orderID: event.order_id,
        eventCode: event.event_code,
      };
    });
  }

  get routes(): ParcelRouteModel[] {
    return this.apiParser.routes.map((route): ParcelRouteModel => {
      const fromLocation = this.events.find((event) => event.location?.name === route.from?.name);
      const toLocation = this.events.find((event) => event.location?.name === route.to?.name);
      return {
        from: {
          ...route.from,
          location: this.getLocationName(fromLocation?.location) || "",
        },
        to: {
          ...route.to,
          location: this.getLocationName(toLocation?.location) || "",
        },
        path: route.path,
        isActual: route.is_actual,
        atd: this.atd,
        etd: this.etd,
        ata: this.ata,
        eta: this.eta,
      };
    });
  }

  public getTransformedEvents(targetEvents: Nullable<ParcelEventModel[]>): ParcelTransformedEventModel[] {
    if (!targetEvents) return [];

    const events: ParcelEventModel[] = [];
    const transformedEvents: ParcelTransformedEventModel[] = [{ location: null, events }];
    let prevLocation: ParcelLocationModel | null = null;

    targetEvents.forEach((event, i) => {
      const location = event.location;
      const locationName = this.getLocationName(location);

      if (i === 0) {
        transformedEvents[0].location = locationName;
        transformedEvents[0].events.push(event);
      } else {
        if (
          prevLocation !== null &&
          prevLocation?.name === location?.name &&
          prevLocation?.state === location?.state &&
          prevLocation?.country_code === location?.country_code
        ) {
          transformedEvents[transformedEvents.length - 1].events.push(event);
        } else {
          transformedEvents.push({ location: locationName, events: [event] });
        }
      }

      prevLocation = location ?? null;
    });

    return transformedEvents;
  }

  private adaptRouteData(): MapRouteModel {
    const routes = this.routes ?? [];
    const lastRoute = routes[routes.length - 1];

    return {
      route: routes
        .filter((route) => route.path?.length)
        .map((route) => ({
          path: route.path,
          type: RouteDataType.LAND,
          transportType: TransportTypes.PARCEL,
        })),
      locations: [
        ...routes.map((route, index) => ({
          id: index,
          name: route.from?.name ?? "",
          country: route.from?.country ?? "",
          countryCode: route.from?.country_code ?? "",
          locode: route.from?.locode,
          lat: route.from?.lat,
          lng: route.from?.lng,
        })),
        ...(lastRoute?.to
          ? [
              {
                name: lastRoute.to.name ?? "",
                country: lastRoute.to.country ?? "",
                countryCode: lastRoute.to.country_code ?? "",
                locode: lastRoute.to.locode,
                lat: lastRoute.to.lat,
                lng: lastRoute.to.lng,
              },
            ]
          : []),
      ],
    };
  }

  get route(): MapRoute | null {
    return this._route;
  }
}
